import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  CheckCircleOutline,
  RocketLaunchOutlined,
  WorkspacePremiumOutlined,
  DescriptionOutlined,
  ReceiptLongOutlined,
  EventNoteOutlined,
  PhotoCameraOutlined,
  DrawOutlined,
  PaymentOutlined,
  AccountBalanceOutlined,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/useAuthStore';
import Logo from '../components/Logo';

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuthStore();

  useEffect(() => {
    // Si l'utilisateur n'est pas connecté, rediriger vers login
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleStart = () => {
    navigate('/dashboard');
  };

  // Calculer le nombre de jours restants de la période d'essai
  const getDaysRemaining = () => {
    if (!user?.trialEndDate) return 30; // Par défaut 30 jours
    const trialEnd = new Date(user.trialEndDate);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const premiumFeatures = [
    {
      icon: <DescriptionOutlined />,
      title: 'Devis illimités',
      description: 'Créez et gérez autant de devis que nécessaire',
    },
    {
      icon: <ReceiptLongOutlined />,
      title: 'Facturation complète',
      description: 'Factures d\'acompte et de solde avec gestion Factur-X',
    },
    {
      icon: <EventNoteOutlined />,
      title: 'Planning de chantier',
      description: 'Organisez vos projets et suivez leur avancement',
    },
    {
      icon: <PhotoCameraOutlined />,
      title: 'Photos de chantier',
      description: 'Stockez et organisez vos photos de travaux',
    },
    {
      icon: <DrawOutlined />,
      title: 'Signature électronique',
      description: 'Faites signer vos devis directement en ligne',
    },
    {
      icon: <PaymentOutlined />,
      title: 'Paiement par carte',
      description: 'Intégration Stripe pour les paiements CB',
    },
    {
      icon: <AccountBalanceOutlined />,
      title: 'Comptabilité',
      description: 'Suivi des paiements et de la trésorerie',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        padding: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'white',
            border: `3px solid ${theme.palette.secondary.main}`,
          }}
        >
          {/* Header avec logo et titre */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Logo size={100} />
            </Box>
            <CheckCircleOutline
              sx={{
                fontSize: 64,
                color: 'success.main',
                mb: 2,
              }}
            />
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              Bienvenue {user?.prenom || user?.nom} !
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Votre compte a été créé avec succès
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Badge Premium */}
          <Box
            sx={{
              textAlign: 'center',
              mb: 3,
              p: 3,
              background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
              borderRadius: 2,
            }}
          >
            <WorkspacePremiumOutlined
              sx={{
                fontSize: 48,
                color: 'white',
                mb: 1,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
              Période d'essai Premium activée !
            </Typography>
            <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
              Profitez de <strong>{getDaysRemaining()} jours</strong> d'accès complet à toutes les
              fonctionnalités Premium
            </Typography>
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
              Aucune carte bancaire requise pendant la période d'essai
            </Typography>
          </Box>

          {/* Liste des fonctionnalités */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}
            >
              Fonctionnalités incluses :
            </Typography>
            <List>
              {premiumFeatures.map((feature, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon sx={{ color: theme.palette.secondary.main }}>
                    {feature.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                    }
                    secondary={feature.description}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Call to action */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleStart}
              startIcon={<RocketLaunchOutlined />}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                },
              }}
            >
              Commencer maintenant
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
              Vous pourrez consulter les détails de votre abonnement à tout moment dans votre profil
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default WelcomePage;
